import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';

import { AuthService } from '@core';
import { VeevaTaxonomyField, VeevaTaxonomySettings } from '@core/api/core';

import { CommonService } from '@shared/services/common.service';
import { VeevaApiError, VeevaService } from '@shared/services/integrations/veeva.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { parseMetadataValue } from '@shared/utils/parse-metadata-value';

@Component({
  selector: 'app-veeva-taxonomy-settings',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MatButton, MatIcon, MatProgressSpinner, MatSlideToggle],
  templateUrl: './veeva-taxonomy-settings.component.html',
  styleUrl: './veeva-taxonomy-settings.component.scss',
})
export class VeevaTaxonomySettingsComponent implements OnInit {
  private readonly _veevaService = inject(VeevaService);
  private readonly _authService = inject(AuthService);
  private readonly _commonService = inject(CommonService);
  private readonly _metadataService = inject(MetadataService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly isAdmin = signal(false);
  protected readonly veevaConfigured = signal(false);
  protected readonly loading = signal(false);
  protected readonly validating = signal(false);
  protected readonly saving = signal(false);
  protected readonly resettingSession = signal(false);
  protected readonly settings = signal<VeevaTaxonomySettings | null>(null);
  protected readonly mainFieldsDraft = signal<string[]>([]);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly showSessionReset = signal(false);

  protected readonly isPersisted = computed(() => this.settings()?.persisted !== false);
  protected readonly validatedAt = computed(() => this.settings()?.validatedAt ?? null);
  protected readonly fields = computed(() => this.settings()?.fields ?? []);
  protected readonly hasMainChanges = computed(() => {
    const saved = this.settings()?.mainFields ?? [];
    const draft = this.mainFieldsDraft();
    if (saved.length !== draft.length) {
      return true;
    }
    const savedSet = new Set(saved);
    return draft.some(name => !savedSet.has(name));
  });

  ngOnInit(): void {
    this._authService
      .user()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(user => {
        this.isAdmin.set(user.roles?.includes('admin') ?? false);
      });

    this._metadataService
      .get<unknown>(MetadataSlugs.VeevaSettings)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: metadata => {
          const configured = this._hasVeevaCredentials(metadata.value);
          this.veevaConfigured.set(configured);
          if (configured) {
            this.loadTaxonomies();
          }
        },
        error: () => {
          this.veevaConfigured.set(false);
        },
      });
  }

  loadTaxonomies(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.showSessionReset.set(false);

    this._veevaService
      .getTaxonomies()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: settings => this._applySettings(settings),
        error: error => this._handleError(error, 'Failed to load Veeva taxonomies'),
        complete: () => this.loading.set(false),
      });
  }

  validateTaxonomies(): void {
    if (!this.isAdmin()) {
      return;
    }

    this.validating.set(true);
    this.errorMessage.set(null);
    this.showSessionReset.set(false);

    this._veevaService
      .validateTaxonomies()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: settings => {
          this._applySettings(settings);
          this._commonService.openSuccessSnackBar('Veeva taxonomies validated successfully');
        },
        error: error => this._handleError(error, 'Failed to validate Veeva taxonomies'),
        complete: () => this.validating.set(false),
      });
  }

  saveMainTaxonomies(): void {
    if (!this.isAdmin() || !this.hasMainChanges()) {
      return;
    }

    this.saving.set(true);
    this.errorMessage.set(null);
    this.showSessionReset.set(false);

    this._veevaService
      .saveMainTaxonomies(this.mainFieldsDraft())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: settings => {
          this._applySettings(settings);
          this._commonService.openSuccessSnackBar('Main Veeva taxonomies saved');
        },
        error: error => this._handleError(error, 'Failed to save main taxonomies'),
        complete: () => this.saving.set(false),
      });
  }

  resetVeevaSession(): void {
    if (!this.isAdmin()) {
      return;
    }

    this.resettingSession.set(true);
    this.errorMessage.set(null);

    this._veevaService
      .resetVeevaSession()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.showSessionReset.set(false);
          this._commonService.openSuccessSnackBar('Veeva session reset — retrying validation');
          this.validateTaxonomies();
        },
        error: error => {
          this._handleError(error, 'Failed to reset Veeva session');
          this.resettingSession.set(false);
        },
        complete: () => this.resettingSession.set(false),
      });
  }

  onCredentialsSaved(): void {
    this.veevaConfigured.set(true);

    if (!this.isAdmin()) {
      this.loadTaxonomies();
      return;
    }

    this.validateTaxonomies();
  }

  toggleMainField(field: VeevaTaxonomyField, event: MatSlideToggleChange): void {
    if (!field.exists) {
      return;
    }

    const next = new Set(this.mainFieldsDraft());
    if (event.checked) {
      next.add(field.name);
    } else {
      next.delete(field.name);
    }
    this.mainFieldsDraft.set([...next]);
  }

  isMainField(field: VeevaTaxonomyField): boolean {
    return this.mainFieldsDraft().includes(field.name);
  }

  private _applySettings(settings: VeevaTaxonomySettings): void {
    this.settings.set(settings);
    this.mainFieldsDraft.set([...settings.mainFields]);
  }

  private _handleError(error: unknown, fallbackMessage: string): void {
    const veevaError = error instanceof VeevaApiError ? error : new VeevaApiError(fallbackMessage, 500);
    this.errorMessage.set(veevaError.message);
    this.showSessionReset.set(veevaError.status === 503);
    this._commonService.openWarningSnackBar(veevaError.message);
  }

  private _hasVeevaCredentials(rawValue: unknown): boolean {
    const settings = parseMetadataValue<{
      username?: string;
      password?: string;
      url?: string;
    }>(rawValue);
    return Boolean(
      settings?.username?.trim() && settings?.password?.trim() && settings?.url?.trim(),
    );
  }
}
